import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiZap, FiAlertCircle, FiX } from 'react-icons/fi';

interface ConnectionSuggestionsButtonProps {
  suggestionsCount: number;
  criticalCount: number;
  onClick: () => void;
  onDismiss?: () => void;
}

/**
 * Bouton flottant qui indique la présence de suggestions de connexion
 * Affiché sur le côté droit du canvas avec animation et badge
 */
export const ConnectionSuggestionsButton: React.FC<ConnectionSuggestionsButtonProps> = ({
  suggestionsCount,
  criticalCount,
  onClick,
  onDismiss,
}) => {
  const { t } = useTranslation('canvas');
  const hasCritical = criticalCount > 0;

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={onClick}
        style={{
        position: 'fixed',
        right: 20,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: hasCritical
          ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        border: 'none',
        boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999,
        transition: 'all 0.3s ease',
        animation: 'pulse-glow 2s ease-in-out infinite',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
        e.currentTarget.style.boxShadow = hasCritical
          ? '0 6px 24px rgba(239, 68, 68, 0.6)'
          : '0 6px 24px rgba(102, 126, 234, 0.6)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
        e.currentTarget.style.boxShadow = hasCritical
          ? '0 4px 16px rgba(239, 68, 68, 0.4)'
          : '0 4px 16px rgba(102, 126, 234, 0.4)';
      }}
      title={t('connectionSuggestions.button.title', { count: suggestionsCount })}
    >
      {/* Icon */}
      {hasCritical ? (
        <FiAlertCircle size={26} style={{ color: 'white' }} />
      ) : (
        <FiZap size={26} style={{ color: 'white' }} />
      )}

      {/* Badge avec count */}
      {suggestionsCount > 0 && (
        <div
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 24,
            height: 24,
            borderRadius: 12,
            background: hasCritical ? '#FBBF24' : '#10B981',
            border: '2px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
            color: hasCritical ? '#78350F' : 'white',
            animation: 'bounce-in 0.5s ease-out',
          }}
        >
          {suggestionsCount > 9 ? '9+' : suggestionsCount}
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
          }
          50% {
            box-shadow: 0 4px 24px rgba(102, 126, 234, 0.6), 0 0 30px rgba(102, 126, 234, 0.3);
          }
        }

        @keyframes bounce-in {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </button>

      {/* Dismiss Button - petit X en bas du bouton */}
      {onDismiss && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          style={{
            position: 'fixed',
            right: 22,
            top: 'calc(50% + 20px)',
            transform: 'translateY(-50%)',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.6)',
            border: '2px solid white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(220, 38, 38, 0.9)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)';
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
          title={t('connectionSuggestions.button.dismiss')}
        >
          <FiX size={14} style={{ color: 'white' }} />
        </button>
      )}
    </div>
  );
};

export default ConnectionSuggestionsButton;
